import keyBy from 'lodash/keyBy';
import reduce from 'lodash/reduce';

import { CreditCardType } from '@pressreader/commerce-types';
import { isNonDigitCharCode, KeyboardIntent } from '@pressreader/utils';

interface CardNumberFormat {
    length: number;
    format: string;
}

const digitFormatPlaceholder = '0';

// For information about another credit card types see:
// https://www.regular-expressions.info/creditcard.html
// https://baymard.com/checkout-usability/credit-card-patterns

// length means length of numeric (non-formatted) value of card number
const defaultFormat = { length: 16, format: '0000 0000 0000 0000' };
const cardTypes = [
    {
        type: CreditCardType.Amex,
        pattern: /^3[47]/,
        formats: [{ length: 15, format: '0000 000000 00000' }],
        luhn: true,
    },
    {
        type: CreditCardType.DinersClub,
        pattern: /^(36|38|30[0-5])/,
        formats: [{ length: 14, format: '0000 000000 0000' }],
        luhn: true,
    },
    {
        type: CreditCardType.Discover,
        pattern: /^(6011|65|64[4-9]|622)/,
        formats: [defaultFormat],
        luhn: true,
    },
    {
        type: CreditCardType.JCB,
        pattern: /^35/,
        formats: [defaultFormat],
        luhn: true,
    },
    {
        type: CreditCardType.MasterCard,
        pattern: /^(5[1-5]|677189|222[1-9]|2[3-6]\d{2}|27[0-1]\d|2720)/,
        formats: [defaultFormat],
        luhn: true,
    },
    {
        type: CreditCardType.Visa,
        pattern: /^4/,
        formats: [{ length: 13, format: '0000 0000 0000 0' }, defaultFormat, { length: 19, format: '0000 0000 0000 0000 000' }],
        luhn: true,
    },
];

const cardTypesMap = keyBy(cardTypes, 'type');

/**
 * Recognizes card type by card number
 */
function recognizeCardType(cardNumber: string): CreditCardType {
    if (!cardNumber) {
        return undefined;
    }

    const cartType = cardTypes.find(t => t.pattern.test(cardNumber));
    return cartType !== undefined ? cartType.type : undefined;
}

function luhnCheck(num: string) {
    const digits = String(num);
    let even = false;
    let sum = 0;
    for (let i = digits.length - 1; i >= 0; i--) {
        let digit = parseInt(digits.charAt(i), 10);
        if (even) {
            digit *= 2;
            if (digit > 9) {
                digit -= 9;
            }
        }
        sum += digit;
        even = !even;
    }

    return sum % 10 === 0;
}

const nonDigitsPattern = /\D/g;

/**
 * Checks if card number syntatically valid.
 * @param {String} cardNumber - card number (digits only)
 */
function validateCardNumber(cardNumber: string): boolean {
    if (!cardNumber) {
        return false;
    }

    const cardType = recognizeCardType(cardNumber);
    const type = cardTypesMap[cardType];

    return (
        type &&
        // check for length
        type.formats.find(f => cardNumber.length === f.length) !== undefined &&
        // luhn
        (!type.luhn || luhnCheck(cardNumber))
    );
}

/**
 * Checks if CVV number valid.
 * @param {String} CVVNumber - card CVV number (digits only)
 */
function validateCardCVVNumber(cardCVVNumber: string): boolean {
    if (!cardCVVNumber) {
        return false;
    }

    return /^\d{3,4}$/.test(cardCVVNumber);
}

const defaultPostalCodeRegex = /^[A-Z\d](?!.* {2})(?!.*-{2})(?!.* -)(?!.*- )[A-Z\d -]{0,18}[A-Z\d]$/i;

const postalCodeVerificators = new Map<string, RegExp>([
    ['US', /^(?!00000)\d{5}(?:[-\s](?!0000)\d{4})?$/],
    ['CA', /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z]([-\s]?)\d[ABCEGHJ-NPRSTV-Z]\d$/i],
    [
        'GB',
        /^(([A-Z]{1,2}\d[A-Z\d]?|ASCN|STHL|TDCU|BBND|[BFS]IQQ|PCRN|TKCA) ?\d[A-Z]{2}|BFPO ?\d{1,4}|(KY\d|MSR|VG|AI)[ -]?\d{4}|[A-Z]{2} ?\d{2}|GE ?CX|GIR ?0A{2}|SAN ?TA1)$/i,
    ],
]);

/**
 * Checks if postal code valid.
 * @param {String} postalCode - postal code
 * @param {String} countryCode - short code of the country
 */
function validatePostalCode(postalCode: string, countryCode?: string): boolean {
    if (!postalCode) {
        return false;
    }

    const verificator = postalCodeVerificators.get(countryCode);
    if (verificator) {
        return verificator.test(postalCode);
    }

    return defaultPostalCodeRegex.test(postalCode);
}

function pickFormat(input: string, formats: CardNumberFormat[]) {
    const inputLength = input.length;
    if (inputLength === 0 || formats.length === 1) {
        return formats[0];
    }

    // take the longest format
    return reduce(formats, (prev, f) => (inputLength > prev.length ? f : prev), formats[0]);
}

function formatByMask(input: string, mask: string): string {
    const maskLength = mask.length;
    const inputLength = input.length;

    const formatted = [];

    let inputPos = 0;
    let formattedPos = 0;
    // all extra characters which exceeds format are stripped
    while ((inputPos < inputLength || mask.charAt(formattedPos) !== digitFormatPlaceholder) && formattedPos < maskLength) {
        if (mask.charAt(formattedPos) === digitFormatPlaceholder) {
            formatted.push(input.charAt(inputPos));
            inputPos++;
        } else {
            formatted.push(mask.charAt(formattedPos));
        }
        formattedPos++;
    }

    return formatted.join('');
}

/**
 * Formats card number, like "1234 5687 9122 4558".
 * @param input - digits only
 * @return formatted string
 */
function formatCardNumber(input: string): string {
    if (!input) {
        return input;
    }

    const number = input.replace(nonDigitsPattern, '');
    const cardType = recognizeCardType(number);
    const type = cardTypesMap[cardType];

    const format = type ? pickFormat(input, type.formats) : defaultFormat;
    return formatByMask(input, format.format);
}

/**
 * Converts card number into digits only string
 * @return digits string
 */
function parseCardNumber(input: string): string {
    if (!input) {
        return input;
    }

    return input.replace(nonDigitsPattern, '');
}

const cardExpFormat = '00 / 0000';

function formatCardExp(input: string): string {
    if (!input) {
        return input;
    }

    input = input.replace(nonDigitsPattern, '');
    return formatByMask(input, cardExpFormat);
}

function parseCardExp(input: string): string {
    if (!input) {
        return input;
    }

    return input.replace(/\s/g, '');
}

const cardExpValidFormat = /\d\d\/\d\d(\d\d){0,1}/;

/**
 * Checks if card number syntatically valid.
 * @param cardExp - card expiration in MM/YY or MM/YYYY format
 */
function validateCardExp(cardExp: string): boolean {
    if (!cardExp) {
        return false;
    }

    const isFormatValid = cardExpValidFormat.test(cardExp);
    if (!isFormatValid) {
        return false;
    }

    const [month, year] = cardExp.split('/');

    const parsedMonth = parseInt(month, 10);
    let parsedYear = parseInt(year, 10);

    const today = new Date();

    if (year.length === 2) {
        const todayYearStr = today.getFullYear().toString();
        parsedYear = parseInt(todayYearStr.substring(0, 2) + year, 10); // in case this code survives until next milenia
    }

    if (parsedYear === today.getFullYear()) {
        return parsedMonth >= today.getMonth() + 1 && parsedMonth <= 12;
    } else if (parsedYear > today.getFullYear()) {
        return parsedMonth > 0 && parsedMonth <= 12;
    }

    return false;
}

function extractCardExp(cardExp: string): { expirationMonth: number; expirationYear: number } {
    if (!validateCardExp(cardExp)) {
        return null;
    }

    const [month, year] = cardExp.split('/');
    let fullYear = year;

    if (fullYear.length === 2) {
        const today = new Date();
        const todayYearStr = today.getFullYear().toString();
        fullYear = todayYearStr.substring(0, 2) + year; // in case this code survives until next milenia
    }

    return { expirationMonth: parseInt(month, 10), expirationYear: parseInt(fullYear, 10) };
}

/**
 * Returns string in format MM/YY or MM/YYYY (important: month should have two digets)
 */
function combineCardExp(expirationMonth: number, expirationYear: number) {
    return `${('0' + expirationMonth).slice(-2)}/${expirationYear}`;
}

/**
 * @param pos current caret position
 */
function getNextCaretPos(intent: KeyboardIntent, pos: number, value: string) {
    if (pos === -1) {
        return -1;
    }

    switch (intent) {
        case KeyboardIntent.Type: {
            pos++;

            while (isNonDigitCharCode(value.charCodeAt(pos))) {
                pos++;
            }

            return pos;
        }
        case KeyboardIntent.Backspace: {
            if (pos === 0) {
                return 0;
            }

            pos--;
            while (isNonDigitCharCode(value.charCodeAt(pos))) {
                pos--;
            }

            return pos;
        }
        case KeyboardIntent.Delete:
        case KeyboardIntent.Cut: {
            while (isNonDigitCharCode(value.charCodeAt(pos))) {
                pos++;
            }

            return pos;
        }
        default:
            return pos;
    }
}

export {
    recognizeCardType,
    validateCardNumber,
    validateCardCVVNumber,
    formatCardNumber,
    parseCardNumber,
    parseCardExp,
    formatCardExp,
    validateCardExp,
    extractCardExp,
    combineCardExp,
    getNextCaretPos,
    validatePostalCode,
};
